<script>
import { getSingleRubric, updateSingleRubric, deleteSingleRubric } from "@/api";
import RubricForm from "@/components/rubrics/rubric_form.vue";
import RubricFullscreenModal from "@/components/rubrics/components/rubric_fullscreen_modal.vue";
import RubricPreviewTab from "@/components/rubrics/components/rubric_preview_tab.vue";
import RubricSettingsTab from "@/components/rubrics/components/rubric_settings_tab.vue";
import { applyRubricSettings } from "@/utils/rubricSettings";
import { GRADE_LEVELS, INTENSITIES } from "@/constants";

const RUBRIC_DATA = {
  grades: GRADE_LEVELS,
  intensities: INTENSITIES,
};

export default {
  name: "EditRubricView",
  components: {
    RubricForm,
    RubricFullscreenModal,
    RubricPreviewTab,
    RubricSettingsTab,
  },
  provide() {
    return {
      // Provide for CHILD components only (Settings, RubricForm, etc)
      newRubricSettings: this.newRubricSettings,
      updateRubricSettings: (key, value) => {
        this.$set(this.newRubricSettings, key, value);
      },
      newRubricData: RUBRIC_DATA,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.hasPreviousRoute = from.name !== null;
    });
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.isDirty) {
      next();
      return;
    }

    const canLeave = await this.$showConfirmationModal({
      title: "Exit rubric editing?",
      text: "Any unsaved changes will be lost. Are you sure you want to exit?",
      okTitle: "Continue Editing",
      cancelTitle: "Exit",
      warningText: "Unsaved changes will be lost",
    });

    next(canLeave ? undefined : false);
  },
  data() {
    return {
      loading: true,
      error: "",
      rubric: null,
      selectedRubric: null,
      isDirty: false,
      isInitialized: false,
      hasPreviousRoute: false,
      newRubricSettings: {
        selectedGradeLevel: "middleschool",
        selectedLanguageId: 1,
        selectedIntensityIdx: 1,
      },
      rubricFormData: {
        name: "",
        type: "Advanced",
        intensity: "moderate",
        criterias: [],
      },
    };
  },
  async mounted() {
    await this.fetchRubric();
  },
  methods: {
    handleFormDataUpdate(formData) {
      this.rubricFormData = formData;
    },

    updateSettingsFromRubric(rubric) {
      // Use shared utility function
      applyRubricSettings(
        rubric,
        (key, value) => {
          this.$set(this.newRubricSettings, key, value);
        },
        RUBRIC_DATA
      );
    },

    async handleSubmit() {
      await this.$refs.rubricForm?.handleSubmit();
    },

    async updateRubric(rubricData) {
      try {
        this.loading = true;

        const selectedIntensity =
          INTENSITIES[this.newRubricSettings.selectedIntensityIdx];

        const updatedRubricData = {
          ...rubricData,
          gradeLevel: this.newRubricSettings.selectedGradeLevel,
          LanguageId: this.newRubricSettings.selectedLanguageId,
          intensity: selectedIntensity?.value || rubricData.intensity,
        };

        await updateSingleRubric(this.$route.params.id, updatedRubricData);

        this.isDirty = false;
        const redirectTo = this.$route?.query?.redirectTo;
        if (redirectTo) {
          await this.$router.push(`/${redirectTo}`);
        } else {
          this.navigateBack();
        }
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },

    async handleDeleteRubric() {
      const isConfirmed = await this.$showDeleteConfirmationModal({
        title: "Are you sure you want to delete this rubric?",
        message:
          "Rubrics cannot be accessed after deletion. This action cannot be undone.",
      });

      if (isConfirmed) {
        this.deleteRubric();
      }
    },

    async deleteRubric() {
      try {
        this.loading = true;
        await deleteSingleRubric(this.$route.params.id);
        this.isDirty = false;
        this.navigateBack();
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },

    handleCancel() {
      // Just navigate - beforeRouteLeave will handle confirmation if needed
      this.navigateBack();
    },

    handleBack() {
      // Just navigate - beforeRouteLeave will handle confirmation if needed
      this.navigateBack();
    },

    navigateBack() {
      if (this.hasPreviousRoute) {
        this.$router.back();
      } else {
        this.$router.push("/rubrics");
      }
    },

    async fetchRubric() {
      try {
        const { data } = await getSingleRubric(this.$route.params.id);

        this.updateSettingsFromRubric(data);
        this.selectedRubric = data;

        this.loading = false;
        this.error = "";

        this.$nextTick(() => {
          this.isInitialized = true;
        });
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },
  },
};
</script>

<template>
  <div>
    <!-- Fullscreen Rubric Edit -->
    <RubricFullscreenModal
      ref="fullscreenModal"
      mode="edit"
      :rubric-name="selectedRubric?.name"
      :show-tabs="true"
      @cancel="handleCancel"
      @save="$refs.rubricForm.handleSubmit()"
      @delete="handleDeleteRubric"
      @back="handleBack"
    >
      <!-- Edit Tab Content -->
      <template #default>
        <rubric-form
          v-if="selectedRubric"
          :key="`template-${selectedRubric.id}`"
          ref="rubricForm"
          :rubric="selectedRubric"
          editing
          :hide-actions="true"
          :initial-data="rubricFormData"
          @rubricSubmitted="updateRubric"
          @formDataUpdate="handleFormDataUpdate"
          @switchToFirstTab="$refs.fullscreenModal.activeTab = 0"
        />
      </template>

      <!-- Preview Tab Content -->
      <template #preview>
        <RubricPreviewTab
          :key="`preview-${rubricFormData.name}-${
            rubricFormData.criterias?.length || 0
          }`"
          :rubric="rubricFormData"
        />
      </template>

      <!-- Settings Tab Content -->
      <template #settings>
        <RubricSettingsTab v-if="selectedRubric" intensity grade language />
      </template>
    </RubricFullscreenModal>
  </div>
</template>
